import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { ActivityIndicator, ScrollView, StyleSheet, View } from "react-native";
import { PdFormLabel } from "src/components/form-control";
import { PdButton, PdButtonLabel } from "src/components/forms/button";
import { PdHookFormErrorMessage, PdHookFormInput, PdHookFormSelect } from "src/components/hook-form";
import { PdStack } from "src/components/layout";

import { PdModal, PdModalCloseButton, PdModalTitle } from "src/components/overlay";
import { useQuery } from "src/hooks/api";
import { useIsMobile } from "src/hooks/ui";
import { kColors } from "src/kColors";
import { kStyleConst } from "src/kStyle";
import { tUseModal, useModal } from "src/providers/OverlayProvider";
import { useAppDispatch } from "src/redux";
import { setupRetailerTerms } from "src/redux/company/actions";
import { tModalComponentProps } from "src/redux/modals/types";
import { getRetailerSetupTerms } from "src/util/apiNew";
import { legacyCountriesOptions, usStateOptions } from "src/util/comboBoxData";
import { tBuyerData } from "tmt_api/src/types/types";

type tModalProps = tModalComponentProps<void>;

type tFormData = {
  businessName: string;
  ein: string;
  address1: string;
  address2?: string;
  city: string;
  state: { value: string; label: string } | null;
  zipCode: string;
  country: { value: string; label: string };
};

const usCountryOption = { label: "United States", value: "US" };

const Form: React.FC<Pick<tModalComponentProps<void>, "onClose" | "onResolve"> & { defaultValue?: tBuyerData }> = ({
  defaultValue,
  onClose,
  onResolve,
}) => {
  const dispatch = useAppDispatch();
  const isMobile = useIsMobile();
  const formMethods = useForm<tFormData>({
    defaultValues: {
      businessName: defaultValue?.businessName || "",
      ein: defaultValue?.ein || "",
      address1: defaultValue?.address1 || "",
      address2: defaultValue?.address2 || "",
      city: defaultValue?.city || "",
      state: defaultValue?.state
        ? usStateOptions.find((state) => [state.label, state.value].includes(defaultValue?.state)) ?? null
        : null,
      zipCode: defaultValue?.zipCode || "",
      country: usCountryOption,
    },
  });

  const handleOnSubmit = formMethods.handleSubmit(async (formData) => {
    await dispatch(
      setupRetailerTerms({
        businessName: formData.businessName,
        ein: formData.ein,
        address1: formData.address1,
        address2: formData.address2,
        city: formData.city,
        state: formData.state?.value || "",
        country: formData.country.value,
        zipCode: formData.zipCode,
      }),
    );
    onResolve();
  });

  const handleOnClose = () => {
    onClose();
  };

  return (
    <FormProvider {...formMethods}>
      <PdStack
        direction={isMobile ? "vertical" : "horizontal"}
        layout={[1, 1]}
        gap={3}
        style={[styles.mb3, styles.wFull]}
      >
        <View>
          <PdFormLabel>Business Name *</PdFormLabel>
          <PdHookFormInput
            name="businessName"
            rules={{ required: "Required" }}
            placeholder="My Business"
            onSubmitEditing={handleOnSubmit}
          />
          <PdHookFormErrorMessage name="businessName" />
        </View>
        <View>
          <PdFormLabel>EIN Number *</PdFormLabel>
          <PdHookFormInput
            name="ein"
            rules={{
              required: "Required",
              pattern: {
                value: /^\d{2}-?\d{7}$/,
                message: "Invalid EIN Number",
              },
            }}
            placeholder="12-3456789"
            onSubmitEditing={handleOnSubmit}
          />
          <PdHookFormErrorMessage name="ein" />
        </View>
      </PdStack>
      <PdStack
        direction={isMobile ? "vertical" : "horizontal"}
        layout={[1, 1]}
        gap={3}
        style={[styles.mb3, styles.wFull]}
      >
        <View>
          <PdFormLabel>Address *</PdFormLabel>
          <PdHookFormInput
            name="address1"
            rules={{ required: "Required" }}
            placeholder="1 Main Street"
            onSubmitEditing={handleOnSubmit}
          />
          <PdHookFormErrorMessage name="address1" />
        </View>
        <View>
          <PdFormLabel>Address 2</PdFormLabel>
          <PdHookFormInput name="address2" placeholder="Suite 1000" onSubmitEditing={handleOnSubmit} />
        </View>
      </PdStack>
      <PdStack
        direction={isMobile ? "vertical" : "horizontal"}
        layout={[2, 2]}
        gap={3}
        style={[styles.mb3, styles.wFull]}
      >
        <View>
          <PdFormLabel>City *</PdFormLabel>
          <PdHookFormInput
            name="city"
            rules={{ required: "Required" }}
            placeholder="New York"
            onSubmitEditing={handleOnSubmit}
          />
          <PdHookFormErrorMessage name="city" />
        </View>
        <View style={styles.formCombobox}>
          <PdFormLabel>State *</PdFormLabel>
          <PdHookFormSelect
            name="state"
            rules={{ required: "Required" }}
            placeholder="New York"
            options={usStateOptions}
            direction="top"
            isClearable={false}
            onSubmitEditing={handleOnSubmit}
          />
          <PdHookFormErrorMessage name="state" />
        </View>
      </PdStack>
      <PdStack
        direction={isMobile ? "vertical" : "horizontal"}
        layout={[2, 2]}
        gap={3}
        style={[styles.mb6, styles.wFull]}
      >
        <View>
          <PdFormLabel>Zip *</PdFormLabel>
          <PdHookFormInput
            name="zipCode"
            rules={{ required: "Required" }}
            placeholder="100001"
            onSubmitEditing={handleOnSubmit}
          />
          <PdHookFormErrorMessage name="zipCode" />
        </View>
        <View style={styles.formCombobox}>
          <PdFormLabel>Country</PdFormLabel>
          <PdHookFormSelect
            name="country"
            rules={{ required: "Required" }}
            placeholder="United States"
            options={legacyCountriesOptions}
            direction="top"
            isClearable={false}
            onSubmitEditing={handleOnSubmit}
            isDisabled
          />
          <PdHookFormErrorMessage name="country" />
        </View>
      </PdStack>
      <PdStack
        direction={isMobile ? "vertical" : "horizontal"}
        isReversed={isMobile}
        layout={[1, 1]}
        gap={3}
        style={styles.wFull}
      >
        <PdButton colorScheme="purple" variant="outline" onPress={handleOnClose}>
          <PdButtonLabel>Cancel</PdButtonLabel>
        </PdButton>
        <PdButton onPress={handleOnSubmit}>
          <PdButtonLabel>Save</PdButtonLabel>
        </PdButton>
      </PdStack>
    </FormProvider>
  );
};

const ModalContent: React.FC<Pick<tModalProps, "onClose" | "onResolve">> = ({ onClose, onResolve }) => {
  const { isLoading, data } = useQuery(getRetailerSetupTerms);

  if (isLoading) {
    return <ActivityIndicator style={styles.modalLoading} color={kColors.brandDarkGreen} size="small" />;
  }

  return (
    <>
      <PdModalTitle style={styles.mb5}>Setup Terms</PdModalTitle>
      <Form defaultValue={data} onClose={onClose} onResolve={onResolve} />
    </>
  );
};

const RetailerSetupTerms: React.FC<tModalProps> = ({ onClose, onResolve }) => {
  const handleOnClose = () => {
    onClose();
  };

  return (
    <PdModal style={styles.modal} onClose={onClose}>
      <PdModalCloseButton onPress={handleOnClose} />
      <ScrollView contentContainerStyle={styles.modalBody}>
        <ModalContent onClose={onClose} onResolve={onResolve} />
      </ScrollView>
    </PdModal>
  );
};

const useRetailerSetupTerms = (): tUseModal<tModalProps> => {
  return useModal(RetailerSetupTerms);
};

export default useRetailerSetupTerms;

const styles = StyleSheet.create({
  formCombobox: {
    zIndex: 1,
  },
  mb3: {
    marginBottom: 3 * kStyleConst.kSpacing,
  },
  mb5: {
    marginBottom: kStyleConst.kSpacing * 5,
  },
  mb6: {
    marginBottom: 6 * kStyleConst.kSpacing,
  },
  modal: {
    marginHorizontal: 8 * kStyleConst.kSpacing,
    maxWidth: 600,
    width: "100%",
  },
  modalBody: {
    alignItems: "flex-start",
    backgroundColor: kColors.lightBrand,
    paddingHorizontal: 6 * kStyleConst.kSpacing,
    paddingVertical: 8 * kStyleConst.kSpacing,
  },
  modalLoading: {
    alignItems: "center",
    height: 250,
    justifyContent: "center",
    width: "100%",
  },
  wFull: {
    width: "100%",
  },
});
